import { apiAxios } from "./axiosInstance";

export const getAllDocuments = async ({
  page = 0,
  limit = 10,
  filters = {},
  exclude_types = [], // Add parameter to exclude document types
} = {}) => {
  try {
    const queryParams = new URLSearchParams({
      skip: (page * limit).toString(),
      limit: limit.toString(),
      search: filters.search || "",
      status: filters.status || "all",
    });

    // Add exclude_types parameter if provided
    if (exclude_types.length > 0) {
      queryParams.append("exclude_types", exclude_types.join(","));
    }

    // document_type filter (proposal, standard, capabilities, etc.)
    if (filters.document_type) {
      queryParams.append("document_type", filters.document_type);
    }

    // period preset (today, yesterday, last_7_days, last_30_days, this_month, last_month)
    if (filters.period) {
      queryParams.append("period", filters.period);
    }

    // explicit date range (created_from, created_to)
    if (filters.created_from) {
      queryParams.append("created_from", filters.created_from);
    }
    if (filters.created_to) {
      queryParams.append("created_to", filters.created_to);
    }

    // sorting (sort_by, sort_order)
    if (filters.sort_by) {
      queryParams.append("sort_by", filters.sort_by);
    }
    if (filters.sort_order) {
      queryParams.append("sort_order", filters.sort_order);
    }

    const response = await apiAxios.get(`/documents?${queryParams}`);

    return {
      items: response.data.items || [],
      total: response.data.total || 0,
      page: response.data.page || 0,
      total_pages: response.data.total_pages || 0,
      has_more: response.data.has_more || false,
    };
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// New Fetcher Function: Get Documents by Current Client ID
export const getMyDocuments = async (exclude_types = []) => {
  try {
    // Convert exclude_types to URL parameters
    const queryParams = new URLSearchParams();
    if (exclude_types.length > 0) {
      queryParams.append("exclude_types", exclude_types.join(","));
    }

    const response = await apiAxios.get(
      `/documents/all/my-documents?${queryParams}`
    );
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getDocument = async (id) => {
  try {
    const response = await apiAxios.get(`/documents/${id}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const createDocument = async (documentData) => {
  try {
    const response = await apiAxios.post("/documents", documentData);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const updateDocument = async ({ id, ...documentData }) => {
  try {
    console.log("fetcher", id);
    console.log("fetcher", documentData);
    const response = await apiAxios.put(`/documents/${id}`, documentData);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const deleteDocument = async (id, type) => {
  try {
    const response = await apiAxios.delete(`/documents/${type}/${id}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const publishDocument = async (id) => {
  try {
    const response = await apiAxios.post(`/documents/${id}/publish`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getPublicDocument = async (id) => {
  try {
    const response = await apiAxios.get(`/documents/public/${id}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// recipientToken is the advisory ?r= from an emailed invoice link. It only tells the
// backend who to name in the first-open notification, so it must be forwarded from the
// browser URL to the API call. It is not a credential: without it the document still
// loads, it just cannot name the viewer.
export const getSharedDocument = async (token, recipientToken) => {
  try {
    const response = await apiAxios.get(`/documents/share/${token}`, {
      headers: {
        Accept: "application/json", // Changed from text/html
      },
      params: recipientToken ? { r: recipientToken } : undefined,
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

//   export const signDocument = async (token, signerName) => {
//     try {
//       const response = await apiAxios.post(`/documents/sign/${token}`, {
//         signer_name: signerName,
//         signature_date: new Date().toISOString()
//       });
//       return response.data;
//     } catch (error) {
//       throw error.response ? error.response.data : error.message;
//     }
//   };

// api_fethcers.js

export const signDocument = async (token, signatureData) => {
  try {
    const response = await apiAxios.post(`/documents/sign/${token}`, {
      signer_name: signatureData.signer_name,
      signature_img: signatureData.signature_img,
      signature_date: signatureData.signature_date,
      selected_payment_option: signatureData.selected_payment_option, // Added payment option
      selected_support_package: signatureData.selected_support_package, // Added support package
      // Multi-signer: which signer slot this signature fills (omitted for legacy single-signer docs)
      ...(signatureData.signer_index !== undefined &&
      signatureData.signer_index !== null
        ? { signer_index: signatureData.signer_index }
        : {}),
    });
    return response.data;
  } catch (error) {
    // Enhanced error handling
    if (error.response) {
      const errorMessage =
        error.response.data.detail || error.response.data.message;
      throw new Error(errorMessage);
    }
    throw new Error("Failed to connect to the server");
  }
};

export const downloadDocumentAsPDF = async (id) => {
  try {
    const response = await apiAxios.get(`/documents/download/${id}`, {
      responseType: "blob", // Ensure we handle binary data
    });

    // Create a link element, trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `document-${id}.pdf`); // Set the file name for download
    document.body.appendChild(link);
    link.click();

    // Clean up the URL object
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download PDF error:", error);
    throw error.response ? error.response.data : error.message;
  }
};

export const unpublishDocument = async (id) => {
  try {
    const response = await apiAxios.post(`/documents/${id}/unpublish`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const createClient = async (clientData) => {
  try {
    const response = await apiAxios.post("/clients", clientData);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to create client");
    }
    throw error;
  }
};

export const getClients = async ({
  page = 0,
  limit = 10,
  filters = {},
} = {}) => {
  try {
    const skip = page * limit;
    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      search: filters.search || "",
      status: filters.status || "all",
      client_type: filters.client_type || "all",
      archived: filters.archived || "active",
    });

    const response = await apiAxios.get(`/clients?${queryParams}`);

    return {
      items: response.data.items || [],
      total: response.data.total || 0,
      page: response.data.page || page,
      total_pages: response.data.total_pages || 0,
      has_more: response.data.has_more || false,
    };
  } catch (error) {
    if (!error.response) {
      throw new Error("Network error occurred. Please check your connection.");
    }

    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to fetch clients");
    }

    throw new Error("An unexpected error occurred while fetching clients");
  }
};

// Add this new fetcher function
export const getClient = async (id) => {
  try {
    const response = await apiAxios.get(`/clients/${id}`);
    return response.data;
  } catch (error) {
    // Enhanced error handling
    if (error.response) {
      const errorMessage =
        error.response.data.detail || error.response.data.message;
      throw new Error(errorMessage);
    }
    throw new Error("Failed to connect to the server");
  }
};

export const createNote = async (clientId, noteContent) => {
  try {
    const response = await apiAxios.post(`/clients/${clientId}/notes`, {
      content: noteContent,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to create note");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Available Services API Functions
export const getAvailableServices = async () => {
  try {
    const response = await apiAxios.get("/services/available");
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error.detail;
  }
};

export const createAvailableService = async (serviceData) => {
  try {
    const response = await apiAxios.post("/services/available", serviceData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error.detail;
  }
};

export const updateAvailableService = async ({ id, ...data }) => {
  try {
    const response = await apiAxios.put(`/services/available/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error.detail;
  }
};

export const deleteAvailableService = async (id) => {
  try {
    const response = await apiAxios.delete(`/services/available/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error.detail;
  }
};

export const addServiceToClient = async (clientId, serviceData) => {
  try {
    const response = await apiAxios.post(
      `/clients/${clientId}/services`,
      serviceData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message || error.detail;
  }
};

// Add this to your api_fetchers.js
export const deleteServiceFromClient = async (clientId, serviceId) => {
  try {
    const response = await apiAxios.delete(
      `/clients/${clientId}/services/${serviceId}`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to delete service");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Update the api_fetchers.js file
export const updateClientPortalAccess = async (clientId, enable) => {
  try {
    const response = await apiAxios.patch(
      `/clients/${clientId}/portal-access`,
      {
        enable: enable,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// A client's LIVE Stripe subscriptions. Distinct from client.services[].pricing,
// which is only what we intended to bill. These are what Stripe is actually charging.
export const getClientSubscriptionsAdmin = async (clientId) => {
  try {
    const response = await apiAxios.get(`/invoices/subscriptions`, {
      params: { client_id: clientId },
    });
    return response.data?.items || [];
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to fetch subscriptions");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Cancels the recurring charge in Stripe and syncs the local records. Idempotent:
// a subscription already cancelled in the Stripe dashboard reconciles instead of erroring.
export const cancelSubscription = async (stripeSubscriptionId) => {
  try {
    const response = await apiAxios.post(
      `/invoices/subscriptions/${stripeSubscriptionId}/cancel`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to cancel subscription");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Issues a fresh portal access code and returns the plaintext once. The backend
// stores only the hash, so this response is the only place the code is readable.
export const issueClientAccessCode = async (clientId) => {
  try {
    const response = await apiAxios.post(
      `/clients/${clientId}/portal-access/code`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to issue access code"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

// Add this to your api_fetchers.js
export const deleteClient = async (clientId) => {
  try {
    const response = await apiAxios.delete(`/clients/${clientId}`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to delete client");
    }
    throw new Error("Failed to connect to the server");
  }
};

// In api_fetchers.js
export const updateServiceToClient = async (
  clientId,
  serviceId,
  serviceData
) => {
  try {
    const response = await apiAxios.put(
      `/clients/${clientId}/services/${serviceId}`,
      serviceData
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to update service");
    }
    throw new Error("Failed to connect to the server");
  }
};

export const getInvoices = async ({
  clientId,
  page = 0,
  limit = 20,
  status = "all",
}) => {
  try {
    const queryParams = new URLSearchParams({
      client_id: clientId,
      skip: (page * limit).toString(),
      limit: limit.toString(),
      status: status,
    });

    const response = await apiAxios.get(`/invoices/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getInvoicesForPortal = async ({
  clientId,
  page = 0,
  limit = 20,
}) => {
  try {
    const queryParams = new URLSearchParams({
      client_id: clientId,
      skip: (page * limit).toString(),
      limit: limit.toString(),
      portal: "true", // Indicate that this request is for the client portal
    });

    const response = await apiAxios.get(`/invoices/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getClientInvoiceDetail = async (invoiceId) => {
  try {
    const response = await apiAxios.get(`/invoices/client/${invoiceId}/detail`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to fetch invoice detail");
    }
    throw new Error(error.message || "An unexpected error occurred");
  }
};

// M13: server-side invoice stats for a client, computed across ALL of that
// client's invoices (not just a capped page). Used by the admin billing view
// so totals stay correct for clients with 20+ invoices.
export const getClientInvoiceStats = async (clientId) => {
  try {
    const response = await apiAxios.get(`/invoices/client/${clientId}/stats`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to fetch invoice stats");
    }
    throw new Error(error.message || "An unexpected error occurred");
  }
};

export const createClientPaymentIntent = async (invoiceId) => {
  try {
    const response = await apiAxios.post(`/invoices/client/${invoiceId}/payment-intent`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to create payment intent");
    }
    throw new Error(error.message || "An unexpected error occurred");
  }
};

export const sendInvoiceEmail = async (invoiceId) => {
  try {
    const response = await apiAxios.post(`/invoices/${invoiceId}/send-email`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to send invoice email");
    }
    throw new Error(error.message || "An unexpected error occurred");
  }
};

export const getClientInvoices = async ({ page = 0, limit = 20 } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      skip: (page * limit).toString(), // Calculate the number of records to skip
      limit: limit.toString(), // Number of records to retrieve
    });

    // Make a GET request to the client-specific invoices route
    const response = await apiAxios.get(
      `/invoices/client/invoices/?${queryParams}`
    );

    // Structure the response to match your frontend's expected format
    return {
      items: response.data.items || [],
      total: response.data.total || 0,
      page: response.data.page || 0,
      total_pages: response.data.total_pages || 0,
      has_more: response.data.has_more || false,
    };
  } catch (error) {
    // Enhanced error handling
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to fetch client invoices"
      );
    }
    throw new Error(error.message || "An unexpected error occurred");
  }
};

export const createInvoice = async (invoiceData) => {
  const response = await apiAxios.post("/invoices", invoiceData);
  return response.data;
};

export const updateInvoice = async ({ id, ...invoiceData }) => {
  try {
    const response = await apiAxios.put(`/invoices/${id}`, invoiceData);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to update invoice");
    }
    throw error;
  }
};

export const deleteInvoice = async (invoiceId) => {
  try {
    const response = await apiAxios.delete(`/invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to delete invoice");
    }
    throw error;
  }
};

export const markInvoiceAsPaid = async ({ invoiceId, paymentData }) => {
  try {
    const response = await apiAxios.post(
      `/invoices/${invoiceId}/mark-as-paid`,
      paymentData
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to mark invoice as paid"
      );
    }
    throw error;
  }
};

export const cancelInvoice = async ({ invoiceId, reason }) => {
  try {
    const response = await apiAxios.post(`/invoices/${invoiceId}/cancel`, {
      reason,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to cancel invoice");
    }
    throw error;
  }
};

export const restoreInvoice = async ({ invoiceId, notes }) => {
  try {
    const response = await apiAxios.post(`/invoices/${invoiceId}/restore`, {
      notes,
    });
    return response.data;
  } catch (error) {
    // Pass through the detailed error message, especially important for Stripe invoice errors
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to restore invoice"
      );
    }
    throw error;
  }
};

export const getDocumentShareUrl = async (document_id) => {
  try {
    const response = await apiAxios.get(`/documents/share-url/${document_id}`);
    return response.data; // Expected to return { share_url: string, token: string }
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

export const getClientDocuments = async (clientId, options = {}) => {
  try {
    const params = new URLSearchParams({
      ...(options.excludeTypes && {
        exclude_types: options.excludeTypes.join(","),
      }),
    });

    const response = await apiAxios.get(
      `/documents/client/${clientId}/documents?${params}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createEngagement = async (clientId, engagementData) => {
  try {
    // POST /clients/{client_id}/engagements
    const response = await apiAxios.post(
      `/clients/${clientId}/engagements`,
      engagementData
    );
    return response.data; // Should be an EngagementInDB object
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to create engagement"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

export const updateEngagement = async (
  clientId,
  engagementId,
  engagementData
) => {
  try {
    const response = await apiAxios.put(
      `/clients/${clientId}/engagements/${engagementId}`,
      engagementData
    );
    return response.data; // This should be the updated EngagementInDB
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to update engagement"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

export const uploadTranscriptToEngagement = async ({
  clientId,
  engagementId,
  transcriptFile,
  speakers = {},
}) => {
  const formData = new FormData();
  formData.append("file", transcriptFile);
  // Convert the speakers dict to JSON string
  formData.append("speakers", JSON.stringify(speakers));

  const response = await apiAxios.post(
    `/clients/${clientId}/engagements/${engagementId}/transcripts`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data; // e.g. { detail, transcript_path, speakers_applied }
};

export const deleteTranscriptFromEngagement = async ({
  clientId,
  engagementId,
  transcriptId,
}) => {
  // Example route: DELETE /clients/{clientId}/engagements/{engagementId}/transcripts/{transcriptId}
  // Adjust if your route is different
  const response = await apiAxios.delete(
    `/clients/${clientId}/engagements/${engagementId}/transcripts/${transcriptId}`
  );
  return response.data; // e.g. returns updated EngagementInDB
};

// api_fetchers.js

export const getTranscriptText = async (
  clientId,
  engagementId,
  transcriptId
) => {
  try {
    const response = await apiAxios.get(
      `/clients/${clientId}/engagements/${engagementId}/transcripts/${transcriptId}/text`
    );
    // The API returns { transcript_text: "..." }
    return response.data; // e.g. { transcript_text: "..." }
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to get transcript text"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

export const generateNotesFromTranscript = async ({
  clientId,
  engagementId,
  transcriptId,
}) => {
  try {
    const response = await apiAxios.post(
      `/clients/${clientId}/engagements/${engagementId}/analyze-transcript/${transcriptId}`
    );
    return response.data; // Returns the updated EngagementInDB with structured notes
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to generate notes from transcript"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

export const deleteEngagement = async (clientId, engagementId) => {
  try {
    const response = await apiAxios.delete(
      `/clients/${clientId}/engagements/${engagementId}`
    );
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(
        error.response.data.detail || "Failed to delete engagement"
      );
    }
    throw new Error("Failed to connect to the server");
  }
};

export const updateClient = async ({ clientId, clientData }) => {
  try {
    const response = await apiAxios.put(`/clients/${clientId}`, clientData);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to update client");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Archive a client (hide from active list)
export const archiveClient = async (clientId) => {
  try {
    const response = await apiAxios.post(`/clients/${clientId}/archive`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to archive client");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Restore an archived client (show in active list again)
export const restoreClient = async (clientId) => {
  try {
    const response = await apiAxios.post(`/clients/${clientId}/restore`);
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw new Error(error.response.data.detail || "Failed to restore client");
    }
    throw new Error("Failed to connect to the server");
  }
};

// Project Management API Functions

// Helper function to handle API errors consistently
const handleApiError = (error) => {
  if (error.response?.data) {
    return new Error(error.response.data.detail || "An error occurred");
  }
  return error;
};

// Get all tasks across all projects
export const getAllTasks = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (filters.status) queryParams.append("status", filters.status);
    if (filters.assigned_to)
      queryParams.append("assigned_to", filters.assigned_to);
    // Always include completed tasks for the timer component
    queryParams.append("include_completed", "true");

    const response = await apiAxios.get(`/tasks/all?${queryParams}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Create a new project
export const createProject = async (projectData) => {
  try {
    const response = await apiAxios.post("/projects", projectData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get list of projects with optional filters
export const getProjects = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add optional filters
    if (filters.clientId) queryParams.append("client_id", filters.clientId);
    if (filters.serviceId) queryParams.append("service_id", filters.serviceId);
    if (filters.status) queryParams.append("status", filters.status);
    // if (filters.includeCompleted) queryParams.append('include_completed', 'true');
    queryParams.append("include_completed", "true");

    const response = await apiAxios.get(`/projects/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get a single project by ID
export const getProject = async (projectId) => {
  try {
    const response = await apiAxios.get(`/projects/${projectId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update a project
export const updateProject = async ({ projectId, ...projectData }) => {
  try {
    const response = await apiAxios.put(`/projects/${projectId}`, projectData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Delete a project
export const deleteProject = async (projectId) => {
  try {
    const response = await apiAxios.delete(`/projects/${projectId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Task Management API Functions

// Create a new task for a project
export const createTask = async ({ projectId, taskData }) => {
  try {
    console.log("project id", projectId);
    const response = await apiAxios.post(`/tasks/${projectId}`, taskData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get all tasks for a project
export const getProjectTasks = async (projectId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add optional filters
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.priority) queryParams.append("priority", filters.priority);
    if (filters.assigned_to)
      queryParams.append("assigned_to", filters.assigned_to);

    queryParams.append("include_completed", "true");

    const response = await apiAxios.get(`/tasks/${projectId}/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get a single task by ID
export const getTask = async (projectId, taskId) => {
  try {
    const response = await apiAxios.get(`/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update a task
export const updateTask = async ({ projectId, taskId, taskData }) => {
  try {
    const response = await apiAxios.put(
      // `/projects/${projectId}/tasks/${taskId}`,
      `/tasks/${taskId}`,
      taskData
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Delete a task
export const deleteTask = async (projectId, taskId) => {
  try {
    const response = await apiAxios.delete(
      // `/projects/${projectId}/tasks/${taskId}`
      `/tasks/${taskId}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Timer Management API Functions

// Start a new timer
export const startTimer = async (data = {}) => {
  try {
    const { projectId, description, billable = true } = data;
    const payload = {
      project_id: projectId,
      description,
      billable,
    };
    const response = await apiAxios.post("/timer/start", payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get user's current running timer
export const getCurrentTimer = async () => {
  // Throw raw errors (e.g. 404) so calling code can distinguish paused vs missing
  const response = await apiAxios.get("/timer/my-timers/current");
  return response.data;
};

// Assign a task to a running timer
export const assignTaskToTimer = async (timerId, taskId, description) => {
  try {
    const payload = {
      task_id: taskId,
    };
    if (description) {
      payload.description = description;
    }

    const response = await apiAxios.post(
      `/timer/assign-task/${timerId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Stop a running timer
export const stopTimer = async (timerId, description) => {
  try {
    const payload = {};
    if (description) {
      payload.description = description;
    }

    const response = await apiAxios.post(`/timer/stop/${timerId}`, payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get recent timers
export const getRecentTimers = async (limit = 10) => {
  try {
    const response = await apiAxios.get(
      `/timer/my-timers/recent?limit=${limit}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Pause a running timer
export const pauseTimer = async (timerId, description) => {
  try {
    const payload = {};
    if (description) {
      payload.description = description;
    }

    const response = await apiAxios.post(`/timer/pause/${timerId}`, payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Resume a paused timer
export const resumeTimer = async (timerId) => {
  try {
    const response = await apiAxios.post(`/timer/resume/${timerId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get time entries for a specific task
export const getTaskTimeEntries = async (taskId) => {
  try {
    const response = await apiAxios.get(`/timer/tasks/${taskId}/time`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get time summary metrics for a task
export const getTaskTimeSummary = async (taskId) => {
  try {
    // Fetch all time entries for the task
    const timeEntries = await getTaskTimeEntries(taskId);

    // Calculate summary metrics on the client side
    const totalDurationMinutes = timeEntries.reduce((total, entry) => {
      // Skip entries that are still running
      if (!entry.end_time) return total;

      // Use duration_minutes if available
      if (entry.duration_minutes) {
        return total + entry.duration_minutes;
      }

      // Calculate duration from start/end times
      const start = new Date(entry.start_time);
      const end = new Date(entry.end_time);
      const durationMs = end - start;
      const durationMinutes = Math.round(durationMs / (1000 * 60));

      return total + durationMinutes;
    }, 0);

    const billableMinutes = timeEntries.reduce((total, entry) => {
      if (!entry.end_time || !entry.billable) return total;

      if (entry.duration_minutes) {
        return total + (entry.billable ? entry.duration_minutes : 0);
      }

      const start = new Date(entry.start_time);
      const end = new Date(entry.end_time);
      const durationMs = end - start;
      const durationMinutes = Math.round(durationMs / (1000 * 60));

      return total + (entry.billable ? durationMinutes : 0);
    }, 0);

    const activeEntries = timeEntries.filter(
      (entry) => entry.status === "running" || entry.status === "paused"
    );

    return {
      totalDurationMinutes,
      billableMinutes,
      totalEntries: timeEntries.length,
      completedEntries: timeEntries.filter((entry) => entry.end_time).length,
      activeEntries: activeEntries.length,
      totalHours: Math.round((totalDurationMinutes / 60) * 10) / 10, // Round to 1 decimal place
      billableHours: Math.round((billableMinutes / 60) * 10) / 10, // Round to 1 decimal place
      timeEntries,
    };
  } catch (error) {
    throw handleApiError(error);
  }
};
